/**
 * PartnerReferralLink — iter98
 * Dedicated page with link, QR, and referral stats.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { Copy, CheckCircle2, Download, Loader2, Store, TrendingUp, Clock, ShieldCheck } from 'lucide-react';
import PartnerAccountLayout from '../../../components/PartnerAccountLayout';
import { API, authHeaders } from './_utils';

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5" data-testid={testId}>
      <Icon className="w-4 h-4 text-gold-300 mb-2" />
      <div className="text-2xl font-black text-white">{value}</div>
      <p className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 mt-1.5 font-bold">{label}</p>
    </div>
  );
}

export default function PartnerReferralLink() {
  const [loading, setLoading] = useState(true);
  const [partner, setPartner] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [overview, setOverview] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [me, refs, dash] = await Promise.all([
          axios.get(`${API}/partners/me`, { headers: authHeaders() }),
          axios.get(`${API}/partners/referrals`, { headers: authHeaders() }),
          axios.get(`${API}/partners/dashboard`, { headers: authHeaders() }),
        ]);
        setPartner(me.data.partner);
        setReferrals(refs.data.items || []);
        setOverview(dash.data.overview);
      } catch {
        toast.error('Could not load referral data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <PartnerAccountLayout currentKey="referral" title="Referral Link & QR Code">
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-gold-300" /></div>
      </PartnerAccountLayout>
    );
  }
  if (!partner) return null;

  const referralLink = `${window.location.origin}/register?partner=${partner.referral_code}`;
  const approved = referrals.filter((r) => ['active', 'paid', 'trial'].includes(r.status)).length;
  const active = overview?.active_restaurants ?? 0;
  const trial = overview?.trial_restaurants ?? 0;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true); toast.success('Copied!');
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error('Copy failed'); }
  };

  const downloadQR = () => {
    const canvas = document.querySelector('[data-testid="partner-referral-qr"] canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `nofee-partner-${partner.referral_code}.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <PartnerAccountLayout
      currentKey="referral"
      title="Referral Link & QR Code"
      subtitle="Share this link or QR code with your members. Every signup is automatically attributed to you."
    >
      <div className="rounded-3xl border border-gold-500/25 bg-gradient-to-br from-gold-500/[0.06] to-black/30 p-6 lg:p-8 mb-8" data-testid="partner-referral-card">
        <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.24em] text-gold-400 font-bold mb-2">Unique Referral Link</p>
            <div className="rounded-xl bg-black/50 border border-white/[0.08] px-4 py-3 font-mono text-sm text-gold-200 break-all mb-4">
              {referralLink}
            </div>
            <p className="text-[10.5px] uppercase tracking-[0.24em] text-gold-400 font-bold mb-1">Partner Code</p>
            <p className="text-2xl font-mono font-black text-white mb-5">{partner.referral_code}</p>
            <div className="flex gap-2 flex-wrap">
              <button onClick={copyLink} className="inline-flex items-center gap-1.5 h-11 px-5 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-bold text-xs whitespace-nowrap transition-all" data-testid="partner-referral-copy">
                {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy Referral Link'}
              </button>
              <button onClick={downloadQR} className="inline-flex items-center gap-1.5 h-11 px-5 rounded-xl border border-gold-500/30 hover:border-gold-500/60 bg-white/[0.02] text-gold-200 font-bold text-xs whitespace-nowrap transition-all" data-testid="partner-referral-download-qr">
                <Download className="w-4 h-4" /> Download QR Code
              </button>
            </div>
          </div>
          <div className="rounded-2xl bg-white p-4 self-center justify-self-center" data-testid="partner-referral-qr">
            <QRCodeCanvas value={referralLink} size={200} level="M" includeMargin={false} />
          </div>
        </div>
        <p className="text-[11px] text-white/45 mt-5 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-gold-300" /> Restaurants signing up via this link get 45 days free + you earn a 10% recurring commission.
        </p>
      </div>

      {/* Stats */}
      <h2 className="text-lg font-bold text-white mb-4">Referral Statistics</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat icon={Store}       label="Restaurant Registrations" value={referrals.length} testId="partner-stat-registrations" />
        <Stat icon={CheckCircle2}label="Approved Restaurants"     value={approved}          testId="partner-stat-approved" />
        <Stat icon={TrendingUp}  label="Active Restaurants"       value={active}            testId="partner-stat-active" />
        <Stat icon={Clock}       label="Trial Restaurants"        value={trial}             testId="partner-stat-trial" />
        <Stat icon={Copy}        label="Partner Code"             value={partner.referral_code} testId="partner-stat-code" />
      </div>

      <p className="mt-5 text-[12px] text-white/45">
        Detailed click tracking is rolling out in a future release. Today, registrations + approvals are tracked automatically.
      </p>
    </PartnerAccountLayout>
  );
}
